import {
  Stack,
  Typography,
  Skeleton,
  Box,
  Tabs,
  Tab,
  Divider,
  Link,
  Button,
  alpha,
  Menu,
  MenuProps,
  styled,
  MenuItem,
} from "@mui/material";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useLocation, useParams } from "react-router-dom";
import history from "utils/history";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import Profile from "./Profile/Profile";
import ContactGroups from "./ContactGroups/ContactGroups";
import ContactCampaigns from "./ContactCampaigns/ContactCampaigns";
import ContactStatusChangeModal from "./Components/ContactStatusChangeModal";
import ContactDelateModal from "./Components/ContactDelateModal";
import { fetchContactById } from "./ViewContactService";
import { handleErrorMessage } from "helpers/methods";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import { KeyboardBackspace } from "@mui/icons-material";
import DropdownDownIcon from "assets/icons1/DropdownDownIcon.svg";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";
import { contactsStatus } from "../ContactsService";
import { StatusColors } from "../ContactsConstants";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

const ViewContact = () => {
  const { id } = useParams<{ id: string }>();
  const getTab = new URLSearchParams(useLocation().search).get("tab");
  const fromArchived = new URLSearchParams(useLocation().search).get(
    "fromArchived"
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChangeStatusModal, setIsChangeStatusModal] =
    useState<boolean>(false);
  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false);
  const [contact, setContact] = useState<any>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [contactsStatusOptions, setContactsStatusOptions] = useState([
    {
      id: -1,
      name: "All status",
    },
  ]);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasContactUpdatePermission = hasAccessTo(
    strings.contact,
    strings.updatePermission
  );

  const tabs = [
    {
      label: "Profile Details",
      value: "Profile Details",
      key: "profile",
      tooltip:
        "Edit and maintain contact details to personalize interactions and streamline outreach.",
    },
    {
      label: "Groups",
      value: "Groups",
      key: "groups",
      tooltip:
        "View and manage the groups this contact belongs to, which may include multiple contacts sharing similar attributes.",
    },
  ];

  const fetchTab = (
    selectedTab: string = "",
    searchBasedOn: "label" | "key"
  ) => {
    const newSelection = tabs.find((tab) => tab[searchBasedOn] === selectedTab);
    return newSelection;
  };
  const tabToSelect = fetchTab(getTab ?? "", "key");
  const [currentTab, setCurrentTab] = useState(
    tabToSelect?.label ?? tabs[0].label
  );

  useEffect(() => {
    const tabToSelect = fetchTab(getTab ?? "", "key");
    setCurrentTab(tabToSelect?.label ?? tabs[0].value);
  }, [getTab]);

  useEffect(() => {
    if (id) getContactData();
  }, []);

  const getContactStatusOptions = async () => {
    try {
      setIsLoading(true);
      const response = await contactsStatus();
      setContactsStatusOptions([...contactsStatusOptions, ...response]);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getContactData = async () => {
    try {
      setIsLoading(true);
      const response = await fetchContactById(+id);
      await getContactStatusOptions();
      setContact(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      const selectedTab = fetchTab(value, "label");
      history.replace(
        `${viewpaths.viewContactViewPath}/${id}?tab=${selectedTab!.key}`
      );
      setCurrentTab(value);
    },
    []
  );

  const handleDialogClose = () => {
    setIsChangeStatusModal(false);
    setIsDeleteModal(false);
  };

  const getContactPageHeader = () => {
    return (
      <>
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" alignItems="center">
            <Link
              color="text.primary"
              href={
                fromArchived
                  ? viewpaths.archivedContactsViewPath
                  : viewpaths.contactsViewPath
              }
              sx={{
                alignItems: "center",
                display: "inline-flex",
              }}
              onClick={(e) => {
                e.preventDefault();
                history.goBack();
              }}
              underline="hover"
              component={RouterLink}
            >
              <KeyboardBackspace
                htmlColor="#475569"
                sx={{ cursor: "pointer", pr: 1 }}
              />
              <Typography variant="body1" color="text.primary" fontWeight={700}>
                {fromArchived ? "Archived" : ""} Contacts
              </Typography>
            </Link>
          </Stack>
        </Stack>

        <Stack direction="column" pt={1}>
          <Stack
            alignItems={{
              xs: "column",
              md: "center",
            }}
            direction={{
              xs: "column",
              md: "row",
            }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction={"column"} spacing={1}>
              <Stack
                direction={{
                  xs: "column",
                  md: "row",
                }}
                spacing={1}
              >
                {isLoading ? (
                  <Skeleton
                    animation="wave"
                    variant="text"
                    height={25}
                    width={250}
                  />
                ) : (
                  <>
                    <Typography
                      color="text.primary"
                      fontSize={"1.375rem"}
                      fontWeight={600}
                    >
                      {contact?.emailId}
                    </Typography>
                    <Typography
                      sx={{
                        color: StatusColors[contact?.status],
                        display: "flex",
                        alignItems: "center",
                      }}
                      variant="subtitle2"
                    >
                      <Box
                        sx={{
                          borderRadius: "50%",
                          height: 6,
                          width: 6,
                          mx: 0.5,
                          backgroundColor: StatusColors[contact?.status],
                        }}
                      />
                      {
                        contactsStatusOptions?.find(
                          (item: any) => item.id === contact?.status
                        )?.name
                      }
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>
            {isLoading ? (
              <Skeleton
                animation="wave"
                variant="text"
                height={25}
                width={100}
              />
            ) : (
              <Stack alignItems="center" direction="row" spacing={2}>
                <CustomInfoTooltip
                  infoMessage={
                    hasContactUpdatePermission
                      ? ""
                      : "You don't have permission to edit contact details"
                  }
                  placement="top"
                >
                  <Button
                    id="action-menu"
                    aria-controls={open ? "action-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    variant="text"
                    disableElevation
                    onClick={handleClick}
                    endIcon={
                      <img src={DropdownDownIcon} alt="DropdownDownIcon" />
                    }
                  >
                    Actions
                  </Button>
                </CustomInfoTooltip>
              </Stack>
            )}
          </Stack>
          <Box>
            <Tabs
              indicatorColor="primary"
              onChange={handleTabsChange}
              scrollButtons="auto"
              textColor="primary"
              value={currentTab}
              variant="scrollable"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                  disabled={isLoading}
                />
              ))}
            </Tabs>
            <Divider />
          </Box>
        </Stack>
      </>
    );
  };

  const getContactPageBody = () => {
    switch (currentTab) {
      case "Profile Details":
        return <Profile />;
      case "Groups":
        return <ContactGroups contact={contact} />;
      case "Campaigns":
        return <ContactCampaigns />;
      case "Activities":
        return;
    }
  };

  const getViewContactPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        {getContactPageHeader()}
        <Box pt={1}>{getContactPageBody()}</Box>
      </Stack>
    );
  };

  const getActionModal = () => {
    if (isChangeStatusModal) {
      return (
        <ContactStatusChangeModal
          isVisible={isChangeStatusModal}
          handleDialogClose={handleDialogClose}
          contact={contact}
          getContactData={getContactData}
          status={contact?.status}
        />
      );
    } else if (isDeleteModal) {
      return (
        <ContactDelateModal
          isVisible={isDeleteModal}
          handleDialogClose={handleDialogClose}
          contact={contact}
          getContactData={getContactData}
        />
      );
    }
  };

  return (
    <CenteredAuthenticatedLayout>
      {getViewContactPage()}
      {getActionModal()}
      <StyledMenu
        id="action-menu"
        MenuListProps={{
          "aria-labelledby": "action-menu",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {(contact?.status === 0 || contact?.status === 1) && (
          <MenuItem
            onClick={() => {
              setIsChangeStatusModal(true);
              handleClose();
            }}
            disableRipple
          >
            <span>
              <Button
                id="status_change_btn"
                variant="text"
                size="small"
                disabled={isLoading || !hasContactUpdatePermission}
                onClick={() => setIsChangeStatusModal(true)}
                sx={{ p: "0px !important" }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {contact?.status !== 0 ? "Unsubscribe" : "Subscribe"}
                </Typography>
              </Button>
            </span>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setIsDeleteModal(true);
            handleClose();
          }}
          disableRipple
        >
          <span>
            <Button
              id="delete_contact_btn"
              variant="text"
              size="small"
              disabled={isLoading || !hasContactUpdatePermission}
              onClick={() => setIsDeleteModal(true)}
              sx={{
                p: "0px !important",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                Delete contact
              </Typography>
            </Button>
          </span>
        </MenuItem>
      </StyledMenu>
    </CenteredAuthenticatedLayout>
  );
};

export default ViewContact;
